/**
 * GL-DESIGN-V3 — Dossier tab hero cards.
 *
 *   ScoreCard     — 150px ScoreDial · "Site Safety Dossier · N sheets" · headline · Preview / Regenerate
 *   StakesCard    — penalty exposure (P1) on a navy inset bar, cost-to-correct on a gold fill
 *                   with a white knob, "Fixing it costs about N¢ on the dollar."
 *   RoadmapCard   — each row: severity dot | action + owner | mono due-date pill | Toggle (sets done_at)
 *   EmailCard     — tint card, 28px icon circles: sent / delivered / opened
 *   InternalCard  — dashed border "Only you see this" wrapper for upsell cards
 */
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { EnvelopeIcon, CheckIcon, EyeIcon, SparkleIcon } from '@phosphor-icons/react';
import { api, formatApiError } from '@/lib/api';
import { V3, Card, ScoreDial, OutlinePill, Toggle, Eyebrow } from '@/components/v3/kit';

const money = (v) => (typeof v === 'number' ? `$${v.toLocaleString()}` : '—');

export function ScoreCard({ report, score, onPreview, onRegenerate, busy, online }) {
  const pct = Math.max(0, Math.min(100, Math.round(score ?? 0)));
  const headline = !report ? 'Generate the first dossier' : pct >= 85 ? 'Strong — ready to send' : 'Ready to review and send';
  return (
    <Card padding={22} radius={26} testid="dossier-score-card" style={{ display: 'flex', gap: 22, alignItems: 'center', flexWrap: 'wrap' }}>
      <ScoreDial pct={pct} size={150} label="score" testid="dossier-score-dial" />
      <div style={{ flex: 1, minWidth: 200, display: 'flex', flexDirection: 'column', gap: 10 }}>
        <Eyebrow>Site Safety Dossier{report?.sheet_count ? ` · ${report.sheet_count} sheets` : ''}</Eyebrow>
        <div style={{ fontSize: 20, fontWeight: 700, color: V3.navy, letterSpacing: '-0.02em' }}>{headline}</div>
        {report?.doc_no && (
          <div data-testid="dossier-doc-ref" style={{ fontFamily: V3.mono, fontSize: 12, color: V3.muted }}>
            {report.doc_no} · REV {String(report.version || 1).padStart(2, '0')} · {new Date(report.created_at).toLocaleDateString()}
          </div>
        )}
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 4 }}>
          {report && <OutlinePill onClick={onPreview} icon="file-pdf" testid="dossier-preview-btn">Preview</OutlinePill>}
          <OutlinePill onClick={onRegenerate} icon="sparkle" testid="generate-report-btn">
            {busy ? 'Generating…' : !online ? 'Needs connection' : report ? 'Regenerate' : 'Generate Dossier'}
          </OutlinePill>
        </div>
      </div>
    </Card>
  );
}

export function StakesCard({ report }) {
  const exposure = report?.exposure ?? null;
  const low = report?.fix_cost_low ?? null;
  const high = report?.fix_cost_high ?? null;
  const cents = report?.cents_on_dollar ?? null;
  const mid = low != null ? (low + (high ?? low)) / 2 : null;
  const frac = exposure && mid != null ? Math.min(1, mid / exposure) : 0;
  return (
    <Card padding={22} radius={26} testid="dossier-stakes-card" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <Eyebrow color={V3.muted}>If OSHA inspected today</Eyebrow>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <span style={{ fontSize: 13.5, color: V3.ink }}>Penalty exposure</span>
        <span data-testid="dossier-exposure" style={{ fontFamily: V3.mono, fontSize: 20, fontWeight: 700, color: V3.p1 }}>{money(exposure)}</span>
      </div>
      <div style={{ height: 14, borderRadius: 999, background: V3.surface, boxShadow: V3.insetSm, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: exposure ? '100%' : '0%', background: V3.navy, borderRadius: 999 }} />
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <span style={{ fontSize: 13.5, color: V3.ink }}>Estimated cost to correct</span>
        <span data-testid="dossier-fix-cost" style={{ fontFamily: V3.mono, fontSize: 15, fontWeight: 700, color: V3.ink }}>
          {low != null ? `${money(low)}${high != null && high !== low ? ` – ${money(high)}` : ''}` : 'Not estimated'}
        </span>
      </div>
      <div style={{ position: 'relative', height: 14, borderRadius: 999, background: V3.surface, boxShadow: V3.insetSm }}>
        <div style={{ height: '100%', width: `${Math.max(frac * 100, low != null ? 3 : 0)}%`, background: V3.gold, borderRadius: 999 }} />
        {low != null && (
          <span style={{ position: 'absolute', top: -4, left: `calc(${Math.max(frac * 100, 3)}% - 11px)`, width: 22, height: 22, borderRadius: '50%', background: '#fff', boxShadow: 'var(--v3-shadow-toggle-knob)' }} />
        )}
      </div>
      <div data-testid="dossier-cents" style={{ fontSize: 15, fontWeight: 600, color: V3.navy, marginTop: 4 }}>
        {cents != null ? `Fixing it costs about ${cents}¢ on the dollar.` : 'Add fix-cost estimates to findings to show ¢ on the dollar.'}
      </div>
    </Card>
  );
}

function dueLabel(iso) {
  if (!iso) return 'No date';
  const d = new Date(`${iso.slice(0, 10)}T12:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' }).toUpperCase();
}

export function RoadmapCard({ eng, reloadKey }) {
  const [rows, setRows] = useState(null);
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    let alive = true;
    api
      .get(`/engagements/${eng.id}/findings`)
      .then((r) => alive && setRows(r.data || []))
      .catch(() => alive && setRows([]));
    return () => {
      alive = false;
    };
  }, [eng.id, reloadKey]);

  async function toggle(f, on) {
    setBusyId(f.id);
    const payload = on
      ? { done_at: new Date().toISOString(), status: 'closed', is_resolved: true }
      : { done_at: '', status: 'open', is_resolved: false };
    try {
      const { data } = await api.patch(`/findings/${f.id}`, payload);
      setRows((arr) => arr.map((x) => (x.id === f.id ? data : x)));
      toast.success(on ? `Finding ${String(f.number).padStart(2, '0')} marked done` : `Finding ${String(f.number).padStart(2, '0')} reopened`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyId(null);
    }
  }

  const list = (rows || []).slice().sort((a, b) => (a.due_date || a.corrective_due_date || '9') .localeCompare(b.due_date || b.corrective_due_date || '9') || a.number - b.number);
  const done = list.filter((f) => f.done_at || f.status === 'closed' || f.status === 'verified_closed').length;

  return (
    <Card padding={22} radius={26} testid="dossier-roadmap-card">
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 12 }}>
        <div>
          <Eyebrow color={V3.muted}>90-day roadmap</Eyebrow>
          <div style={{ fontSize: 18, fontWeight: 700, color: V3.navy, marginTop: 4 }}>Corrective actions</div>
        </div>
        <span data-testid="dossier-roadmap-progress" style={{ fontFamily: V3.mono, fontSize: 12.5, fontWeight: 700, color: V3.muted }}>
          {rows ? `${done} of ${list.length} done` : '…'}
        </span>
      </div>
      {rows && list.length === 0 && <div style={{ fontSize: 14, color: V3.muted }}>No findings yet — log one on the Findings tab.</div>}
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {list.map((f, i) => {
          const isDone = !!(f.done_at || f.status === 'closed' || f.status === 'verified_closed');
          const color = f.priority === 'P1' ? V3.p1 : f.priority === 'P3' ? V3.p3 : V3.p2;
          const action = (f.corrective_action || '').split(/(?<=[.!?])\s+/)[0] || f.violation_type || `Finding ${f.number}`;
          return (
            <div key={f.id} data-testid={`roadmap-row-${f.id}`} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '12px 0', borderTop: i ? '1px solid rgba(20,38,84,.08)' : 0 }}>
              <span style={{ width: 10, height: 10, borderRadius: '50%', background: color, flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14.5, fontWeight: 600, color: V3.ink, textDecoration: isDone ? 'line-through' : 'none', opacity: isDone ? 0.55 : 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  <span style={{ fontFamily: V3.mono, color: V3.navy, marginRight: 8 }}>{String(f.number).padStart(2, '0')}</span>
                  {action}
                </div>
                <div style={{ fontSize: 12.5, color: V3.muted, marginTop: 2 }}>{f.owner || f.suggested_responsible_party || 'Owner not set'}</div>
              </div>
              <span style={{ fontFamily: V3.mono, fontSize: 11.5, fontWeight: 700, color: V3.navy, background: V3.tint, borderRadius: 999, padding: '6px 10px', whiteSpace: 'nowrap' }}>
                {dueLabel(f.due_date || f.corrective_due_date)}
              </span>
              <Toggle value={isDone} disabled={busyId === f.id} onChange={(v) => toggle(f, v)} testid={`roadmap-toggle-${f.id}`} />
            </div>
          );
        })}
      </div>
    </Card>
  );
}

export function EmailCard({ report }) {
  const steps = [
    { icon: EnvelopeIcon, label: 'Sent', at: report?.last_sent_at, to: report?.last_sent_to },
    { icon: CheckIcon, label: 'Delivered', at: report?.last_delivered_at },
    { icon: EyeIcon, label: 'Opened', at: report?.last_opened_at, count: report?.open_count },
  ];
  return (
    <div data-testid="dossier-email-card" style={{ background: V3.tint, borderRadius: 26, padding: 22, display: 'flex', flexDirection: 'column', gap: 12 }}>
      <Eyebrow color={V3.muted}>Email tracking</Eyebrow>
      {!report?.last_sent_at && <div style={{ fontSize: 14, color: V3.muted }}>Not sent yet. The client gets the share link and PDF; opens are tracked here.</div>}
      {steps.map((s) => {
        const Icon = s.icon;
        const hit = !!s.at;
        return (
          <div key={s.label} style={{ display: 'flex', alignItems: 'center', gap: 12, opacity: report?.last_sent_at ? 1 : 0.45 }}>
            <span style={{ width: 28, height: 28, borderRadius: '50%', background: hit ? V3.navy : '#fff', color: hit ? '#fff' : V3.muted, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Icon size={14} weight="bold" />
            </span>
            <div style={{ flex: 1, fontSize: 14, fontWeight: 600, color: V3.ink }}>
              {s.label}
              {s.to && <span style={{ fontWeight: 400, color: V3.muted }}> · {s.to}</span>}
              {s.count > 1 && <span style={{ fontWeight: 400, color: V3.muted }}> · {s.count}×</span>}
            </div>
            <span style={{ fontFamily: V3.mono, fontSize: 11.5, color: V3.muted }}>{hit ? new Date(s.at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—'}</span>
          </div>
        );
      })}
      {report?.last_bounced_at && <div style={{ fontSize: 13, fontWeight: 700, color: V3.p1 }}>Bounced {new Date(report.last_bounced_at).toLocaleString()}</div>}
    </div>
  );
}

export function InternalCard({ children }) {
  return (
    <div data-testid="dossier-internal-card" style={{ border: `1.5px dashed rgba(20,38,84,.35)`, borderRadius: 26, padding: 22, display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <SparkleIcon size={14} weight="fill" color={V3.goldDim} />
        <Eyebrow color={V3.goldDim}>Only you see this</Eyebrow>
      </div>
      {children}
    </div>
  );
}
